import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { uploadsApi, type UploadCategory } from '@/api/uploads';
import { humanizeError } from '@/lib/errors';

/**
 * Поле картинки: файл загружается К НАМ и остаётся адресом в форме.
 *
 * ⚠️ ЗАЧЕМ ЗАГРУЗКА, А НЕ ССЫЛКА. До 28.08.2026 обложка курса задавалась
 * ссылкой, и в боевых курсах там стоял адрес ЧУЖОГО сервиса. Картинка тянулась
 * оттуда, где мы ничего не контролируем: удалят файл или закроют доступ — и
 * обложка пропадёт у всех сразу, без предупреждения.
 *
 * Поле ссылки при этом ОСТАЁТСЯ видимым и редактируемым: у существующих курсов
 * там уже стоят адреса, и прятать их значило бы сделать вид, что их нет.
 */

export type ImageFieldProps = {
  label: string;
  /** Текущее значение — адрес картинки. */
  value: string;
  onChange: (url: string) => void;
  /** Куда складывать файл на нашей стороне. */
  category: UploadCategory;
  hint?: string;
};

export function ImageField({ label, value, onChange, category, hint }: ImageFieldProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = async (file: File) => {
    setError(null);

    // Проверяем ДО отправки: иначе человек ждёт загрузку десяти мегабайт, чтобы
    // услышать отказ от сервера.
    if (!file.type.startsWith('image/')) {
      setError(t('imageField.errorNotImage'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError(t('imageField.errorTooBig'));
      return;
    }

    setBusy(true);
    try {
      const res = await uploadsApi.upload(file, category);
      onChange(res.url);
    } catch (e) {
      setError(humanizeError(e));
    } finally {
      setBusy(false);
      // Сбрасываем выбор файла: без этого выбор ТОГО ЖЕ файла второй раз не
      // вызовет событие, и повторная попытка после ошибки молча ничего не
      // сделает.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-on-surface">{label}</label>

      <div className="flex items-start gap-3">
        {/* Предпросмотр: сразу видно, что загрузилось именно то и что адрес живой. */}
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-outline-variant bg-surface-variant/40">
          {value ? (
            <img
              src={value}
              alt=""
              className="h-full w-full object-cover"
              onError={(e) => {
                // Битый адрес не должен рисовать сломанный значок — показываем
                // пустое место, а рядом остаётся сама строка адреса.
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : null}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void pick(f);
            }}
          />

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              className="inline-flex h-9 items-center gap-1.5 rounded-button bg-secondary-container/30 px-3 text-sm font-medium text-on-secondary-container hover:bg-secondary-container/50 disabled:opacity-60"
            >
              <span className="material-symbols-outlined text-[18px]">upload_file</span>
              {busy ? t('imageField.uploading') : t('imageField.upload')}
            </button>

            {value && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="inline-flex h-9 items-center rounded-button px-3 text-sm text-on-surface-variant hover:bg-surface-container-low"
              >
                {t('imageField.clear')}
              </button>
            )}
          </div>

          <input
            type="url"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://…"
            className="w-full rounded-xl border border-outline-variant bg-surface p-2 text-sm"
          />
        </div>
      </div>

      {hint && !error && <p className="mt-1 text-xs text-on-surface-variant">{hint}</p>}
      {error && <p className="mt-1 text-xs text-error">{error}</p>}
    </div>
  );
}
